import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad from './SignaturePad';
import Button from '../../Elements/Button';

/**
 * Firmar el acta HABIÉNDOLA LEÍDO.
 *
 * Antes esto era un recuadro de firma suelto en mitad del alta: el socio
 * estampaba el dedo en un documento que no había visto. Ahora el botón abre el
 * acta —el PDF del club, o la genérica si no ha subido el suyo— con la firma al
 * final: para llegar a firmar hay que pasar por el documento.
 * (ACTA_FIRMA_PLAN.md F2, petición de Antonio 01-09-2026)
 *
 * La vista previa la pinta el MISMO código del backend que genera el acta que se
 * guarda (`acta_render`), así que lo que se lee es literalmente lo que se firma.
 *
 * El componente no sabe de dónde sale el PDF: quien lo usa le pasa `pedirActa`.
 * En el alta va con el token del staff; en el formulario público del QR, por el
 * `venue_token` y sin sesión. Aquí solo vive el flujo leer → firmar → cerrar.
 *
 * @param pedirActa        () => Promise<Blob> con el PDF a leer.
 * @param datos            lo que se firma; su huella decide si la firma caduca.
 * @param caducaAlCambiar  false para la firma hecha en el móvil del socio: el club
 *                         completa datos días después y no puede pedirle que vuelva
 *                         a firmar (decidido 02-09-2026).
 * @param textos           rótulos, para el formulario público multiidioma.
 * @param confirmarLectura exige marcar "he leído" antes de habilitar la firma.
 *                         Obligatorio en móvil: Chrome de Android NO pinta PDFs
 *                         en un iframe y Safari de iOS enseña solo la primera
 *                         página, así que tener el visor delante no prueba que
 *                         se haya visto el documento. En la tablet del club se
 *                         ve entero y no hace falta.
 */
// El backend responde el PDF como blob, así que `err.response.data` NO es el JSON
// del error sino un Blob: leer `.detail` da undefined y el motivo real se pierde.
async function detalleDeError(err, porDefecto) {
  const data = err?.response?.data;
  try {
    if (data instanceof Blob) {
      const texto = await data.text();
      return JSON.parse(texto)?.detail || porDefecto;
    }
    return data?.detail || porDefecto;
  } catch {
    return porDefecto;
  }
}

export default function FirmarDocumento({
  pedirActa,
  datos,
  value,
  onChange,
  caducaAlCambiar = true,
  confirmarLectura = false,
  textos = {},
}) {
  const T = {
    firmar: 'Firmar documento',
    firmado: '✓ Documento firmado',
    verOFirmar: 'Ver o volver a firmar',
    titulo: 'Acta de inscripción',
    subtitulo: 'Lee el documento y firma al final.',
    cargando: 'Cargando el documento…',
    errorCarga: 'No se pudo cargar el documento. Sin documento no se puede firmar.',
    tuFirma: 'Firma del socio',
    firmaAyuda: 'Firma con el dedo dentro del recuadro.',
    cancelar: 'Cancelar',
    firmarYCerrar: 'Firmar y cerrar',
    cerrarSinFirmar: 'Cerrar sin firmar',
    abrirAparte: 'Abrir el documento en otra pestaña',
    descargarDocumento: 'Descargar el documento para leerlo',
    avisoSinVisor:
      'Este navegador no puede mostrar el PDF aquí. Descárgalo, léelo desde la ' +
      'notificación de descarga y vuelve: la firma se hace en el recuadro de abajo, ' +
      'no sobre el PDF.',
    heLeido: 'He leído el documento y estoy de acuerdo',
    faltaLeer: 'Falta marcar «He leído el documento» para poder firmar.',
    faltaFirmar: 'Falta tu firma en el recuadro.',
    irAFirmar: 'Ir a firmar ↓',
    avisoMovil:
      'Si el documento no se ve aquí, ábrelo en otra pestaña con el enlace de arriba ' +
      'y vuelve para firmar: la firma se hace en el recuadro de abajo, no sobre el PDF.',
    reintentar: 'Reintentar',
    caducada:
      'Has cambiado datos del socio después de firmar, así que la firma ya no vale: ' +
      'el acta que se guardaría no sería la que se firmó. Hay que volver a firmar.',
    ...textos,
  };
  // Chrome en Android no trae visor de PDF: el iframe pinta SU recuadro gris
  // con un botón «Abrir» que dentro de un iframe está muerto, y una pestaña con
  // URL blob: tampoco es fiable (su gestor de descargas no puede leerla — en un
  // dispositivo no abría nada; verificado en móviles reales el 03-09-2026). El
  // API estándar lo delata: pdfViewerEnabled === false. En ese caso, nada de
  // iframe y descarga de verdad (atributo download, que el renderizador sí sabe
  // hacer). Safari y escritorio, donde el visor existe, siguen como estaban.
  const sinVisorPdf = typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === false;

  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [reintentable, setReintentable] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [firmaBorrador, setFirmaBorrador] = useState(null);
  const urlRef = useRef(null);
  // Con qué datos se firmó. Si el staff corrige el DNI o el nombre DESPUÉS de
  // firmar, el acta que se guarda se pinta con los datos nuevos y la firma
  // vieja: el socio habría firmado un documento distinto del archivado.
  const [firmadoCon, setFirmadoCon] = useState(null);
  const [caducada, setCaducada] = useState(false);
  const [leido, setLeido] = useState(false);
  const peticionRef = useRef(0);
  // Adónde llevar al socio cuando no encuentra dónde firmar. Reportado por Kong el
  // 20-09-2026: «no les aparece la firma pero sí el documento» — en el móvil, el
  // visor ocupa la pantalla entera y la casilla queda debajo, fuera de la vista.
  const lecturaRef = useRef(null);
  const firmaRef = useRef(null);

  const huella = useMemo(() => JSON.stringify(datos || {}), [datos]);

  const soltarUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // El blob del PDF se libera al cerrar y al desmontar: sin esto cada apertura
  // deja un documento entero retenido en memoria, y en una tablet que encadena
  // altas toda la tarde eso se nota.
  useEffect(() => soltarUrl, [soltarUrl]);

  // Cambian los datos del acta después de firmar -> la firma deja de valer.
  useEffect(() => {
    if (!caducaAlCambiar) return;
    if (!value || firmadoCon === null || firmadoCon === huella) return;
    setCaducada(true);
    setFirmadoCon(null);
    onChange?.(null);
  }, [huella, value, firmadoCon, onChange, caducaAlCambiar]);

  const abrir = async () => {
    // Cada apertura lleva su número: si se cierra a media carga, la respuesta que
    // llega tarde no puede repintar el PDF de ANTES de corregir los datos.
    const miPeticion = peticionRef.current + 1;
    peticionRef.current = miPeticion;

    setError('');
    setReintentable(false);
    setCaducada(false);
    // Reabrir un documento YA firmado no exige reconfirmar la lectura: esa
    // conformidad se dio al firmar ESTE documento, y sin ella la firma ni
    // siquiera se ve (el recuadro va detrás de la casilla) — reportado el
    // 03-09: «pulso Documento firmado y no aparece la firma». Si la firma
    // caducó o no existe, value es null y la casilla vuelve a exigirse.
    setLeido(Boolean(value));
    setCargando(true);
    setFirmaBorrador(value || null);
    setAbierto(true);
    try {
      const blob = await pedirActa();
      if (peticionRef.current !== miPeticion) return;
      soltarUrl();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setPdfUrl(url);
    } catch (err) {
      if (peticionRef.current !== miPeticion) return;
      setError(await detalleDeError(err, T.errorCarga));
      // Repetir solo ayuda si falló el camino; un 400/422 dará lo mismo otra vez.
      const status = err?.response?.status;
      setReintentable(!status || status >= 500 || status === 408 || status === 429);
      setPdfUrl(null);
    } finally {
      if (peticionRef.current === miPeticion) setCargando(false);
    }
  };

  const cerrar = () => {
    peticionRef.current += 1;   // invalida cualquier carga en vuelo
    setAbierto(false);
    setPdfUrl(null);
    soltarUrl();
  };

  const firmarYCerrar = () => {
    setFirmadoCon(huella);
    setCaducada(false);
    onChange?.(firmaBorrador || null);
    cerrar();
  };

  const firmado = Boolean(value);

  // Un botón desactivado que no dice por qué es un callejón sin salida. Kong,
  // 20-09-2026: el socio abría el acta en su móvil, veía el documento, veía
  // «Firmar y cerrar» en gris, y nada más — el recuadro de firma ni siquiera
  // existe hasta marcar la casilla, y la casilla estaba fuera de la pantalla.
  // En la tablet no pasaba porque allí no se pide confirmar la lectura.
  const falta = !pdfUrl
    ? null
    : confirmarLectura && !leido
      ? { texto: T.faltaLeer, destino: lecturaRef }
      : !firmaBorrador
        ? { texto: T.faltaFirmar, destino: firmaRef }
        : null;

  const irAlPasoQueFalta = () => {
    const el = falta?.destino?.current;
    // jsdom no implementa scrollIntoView; el foco sí funciona en los dos sitios y
    // es lo que de verdad hace falta en un lector de pantalla.
    if (typeof el?.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    el?.querySelector?.('input, canvas')?.focus?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        className={
          firmado
            ? 'inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700'
            : 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700'
        }
      >
        {firmado ? T.firmado : T.firmar}
      </button>
      {firmado && (
        <button
          type="button"
          onClick={abrir}
          className="ml-2 text-sm font-medium text-gray-600 underline hover:text-gray-800"
        >
          {T.verOFirmar}
        </button>
      )}
      {caducada && !firmado && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {T.caducada}
        </p>
      )}

      {abierto && (
        <div
          className="fixed inset-0 z-[100002] flex items-center justify-center bg-black/70 p-2 sm:p-4"
          role="presentation"
          onKeyDown={(e) => {
            // Sin esto el Esc lo caza el modal de "Crear socio" de detrás y se
            // cierra el alta entera mientras el socio está leyendo el acta.
            if (e.key === 'Escape') {
              e.stopPropagation();
              cerrar();
            }
          }}
        >
          <div className="flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{T.titulo}</h3>
                <p className="text-sm text-gray-500">
                  {T.subtitulo}
                </p>
              </div>
              <button
                type="button"
                onClick={cerrar}
                aria-label={T.cerrarSinFirmar}
                className="rounded-full px-3 py-1 text-2xl text-gray-500 hover:bg-gray-100"
              >
                ×
              </button>
            </div>

            {/* El documento y la firma van en el MISMO scroll: para llegar a la
                casilla de firma hay que bajar por el acta entera. */}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {cargando && (
                <div className="py-10 text-center text-sm text-gray-500">{T.cargando}</div>
              )}

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {error}
                  {/* Sin esto, un corte de red dejaba al socio sin salida: cerrar y reabrir. */}
                  {!cargando && reintentable && (
                    <Button variant="outline" size="sm" onClick={abrir} className="mt-3">
                      {T.reintentar}
                    </Button>
                  )}
                </div>
              )}

              {pdfUrl && (sinVisorPdf ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-center">
                  <p className="mb-4 text-sm text-gray-700">{T.avisoSinVisor}</p>
                  <a
                    href={pdfUrl}
                    download="acta.pdf"
                    className="inline-block rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    {T.descargarDocumento}
                  </a>
                </div>
              ) : (
                <>
                  {/* El visor incrustado no es fiable en móvil, así que SIEMPRE hay
                      una forma de abrir el PDF de verdad. */}
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-2 inline-block text-sm font-medium text-blue-700 underline"
                  >
                    {T.abrirAparte}
                  </a>
                  <iframe
                    src={pdfUrl}
                    title={T.titulo}
                    // 45vh en móvil: con 70vh el visor se comía la pantalla y la
                    // casilla quedaba por debajo del pliegue. En iOS además el
                    // iframe se traga el gesto de scroll, así que el socio no
                    // podía ni bajar a buscarla.
                    className="h-[45vh] w-full rounded-xl border border-gray-200 sm:h-[70vh]"
                  />
                  {confirmarLectura && (
                    <p className="mt-2 text-xs text-gray-500">{T.avisoMovil}</p>
                  )}
                </>
              ))}

              {pdfUrl && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  {confirmarLectura && (
                    <label
                      ref={lecturaRef}
                      className="mb-3 flex items-start gap-2 text-sm text-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={leido}
                        onChange={(e) => setLeido(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>{T.heLeido}</span>
                    </label>
                  )}
                  {(!confirmarLectura || leido) && (
                    <div ref={firmaRef}>
                      <h4 className="text-sm font-semibold text-gray-800">{T.tuFirma}</h4>
                      <p className="mb-2 text-xs text-gray-500">
                        {T.firmaAyuda}
                      </p>
                      <SignaturePad value={firmaBorrador} onChange={setFirmaBorrador} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-4 py-3">
              {falta && (
                <button
                  type="button"
                  onClick={irAlPasoQueFalta}
                  className="mr-auto flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-left text-xs text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100"
                >
                  <span>{falta.texto}</span>
                  <span className="shrink-0 font-semibold underline">{T.irAFirmar}</span>
                </button>
              )}
              <button type="button" onClick={cerrar} className="wa-btn-secondary">
                {T.cancelar}
              </button>
              <button
                type="button"
                onClick={firmarYCerrar}
                disabled={!firmaBorrador || (confirmarLectura && !leido)}
                className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {T.firmarYCerrar}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
